import { useState, useEffect, useMemo, useRef } from 'react'
import { motion, AnimatePresence, useDragControls, type PanInfo } from 'motion/react'
import { SheetGrip } from '../SheetGrip'
import { X, Utensils, Check, AlertCircle, Plus } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { NUTRITION_METRICS } from '@tfa/shared'
import { api } from '../../services/api'
import { RewardCongrats } from './RewardCongrats'
import { FiberInfoSheet } from './FiberInfoSheet'
import { AddMetricsSheet } from './AddMetricsSheet'

const F = 'Roboto, sans-serif'

export interface DayNutritionValues {
  calories: string
  protein: string
  fat: string
  carbs: string
  fiber: string
}

interface Props {
  open: boolean
  onClose: () => void
  date: string
  initialValues: DayNutritionValues
  /** Включённые метрики (ключи NUTRITION_METRICS). Клетчатка тоже здесь. */
  enabledMetrics?: string[]
  /** Начальные значения доп. метрик за день (из DiaryEntry.metrics). */
  metricsInitial?: Record<string, number>
  /** Колбэк при изменении набора метрик — родитель может обновить кэш. */
  onEnabledChange?: (enabled: string[]) => void
  /** FatSecret подключён — тогда подсказку «Подключи FatSecret» не показываем. */
  fatSecretConnected?: boolean
}

interface FieldProps {
  label: string
  value: string
  unit: string
  color: string
  testid: string
  error?: string
  onChange: (v: string) => void
  /** Если задан — рядом с лейблом показывается кнопка со справкой. */
  onInfo?: () => void
  /** Текст кнопки справки. Если задан — вместо крошечного «?» рисуется
   *  заметная цветная пилюля (напр. «Как добрать?» для клетчатки, #264). */
  infoLabel?: string
  /** Если задан — справа показывается крестик «убрать метрику». */
  onRemove?: () => void
}

function NutrField({ label, value, unit, color, testid, error, onChange, onInfo, infoLabel, onRemove }: FieldProps) {
  return (
    <div>
      <div style={{
        background: 'rgba(255,255,255,0.9)',
        borderRadius: 14,
        padding: '10px 12px',
        border: `1px solid ${error ? '#FF3B30' : `${color}28`}`,
        display: 'flex', alignItems: 'center', gap: 10,
        transition: 'border-color 0.15s',
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4 }}>
            <div style={{ fontSize: 11, color: error ? '#FF3B30' : '#9AA5B4', fontWeight: 600, fontFamily: F }}>{label}</div>
            {onInfo && (
              // #264: с infoLabel — заметная цветная пилюля «Как добрать?» (инфа
              // ценная, крошечный «?» пользователи не замечали); иначе — старый «?».
              infoLabel ? (
                <button
                  type="button"
                  data-testid={`${testid}-info`}
                  onClick={(e) => { e.stopPropagation(); onInfo() }}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 3, flexShrink: 0,
                    height: 18, padding: '0 8px', borderRadius: 9, border: 'none',
                    background: `${color}1F`, color, fontSize: 10.5, fontWeight: 800,
                    cursor: 'pointer', fontFamily: F, lineHeight: 1, letterSpacing: 0.1,
                  }}
                >
                  💡 {infoLabel}
                </button>
              ) : (
                <button
                  type="button"
                  data-testid={`${testid}-info`}
                  onClick={(e) => { e.stopPropagation(); onInfo() }}
                  aria-label={`Что такое ${label}`}
                  style={{
                    width: 15, height: 15, borderRadius: '50%', border: 'none', flexShrink: 0,
                    background: `${color}22`, color, fontSize: 10, fontWeight: 800,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', fontFamily: F, lineHeight: 1, padding: 0,
                  }}
                >?</button>
              )
            )}
          </div>
          <input
            data-testid={testid}
            value={value}
            onChange={(e) => onChange(e.target.value.replace(/[^0-9.,]/g, '').replace(',', '.'))}
            inputMode="decimal"
            placeholder="0"
            onFocus={(e) => {
              // Когда iOS-клавиатура выезжает, прокручиваем поле в центр видимой области,
              // чтобы оно не оказалось под клавиатурой.
              const el = e.currentTarget
              setTimeout(() => el.scrollIntoView({ block: 'center', behavior: 'smooth' }), 250)
            }}
            style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 20, fontWeight: 700, color: '#1C2D41', fontFamily: F, width: '100%', padding: 0 }}
          />
        </div>
        <span style={{ fontSize: 13, color, fontWeight: 700, flexShrink: 0 }}>{unit}</span>
        {onRemove && (
          <button
            type="button"
            data-testid={`${testid}-remove`}
            onClick={(e) => { e.stopPropagation(); onRemove() }}
            aria-label={`Убрать ${label}`}
            style={{
              width: 22, height: 22, borderRadius: '50%', border: 'none', flexShrink: 0,
              background: 'rgba(0,0,0,0.05)', color: '#9AA5B4',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0,
            }}
          >
            <X size={13} />
          </button>
        )}
      </div>
      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4, paddingLeft: 12 }}>
          <AlertCircle size={11} color="#FF3B30" />
          <span style={{ fontSize: 11, color: '#FF3B30', fontFamily: F, lineHeight: 1.3 }}>{error}</span>
        </div>
      )}
    </div>
  )
}

// ── Validation ──
const RANGES = {
  calories: { min: 200, max: 10000, label: 'калории' },
  protein:  { min: 0,   max: 500,   label: 'белки'    },
  fat:      { min: 0,   max: 500,   label: 'жиры'     },
  carbs:    { min: 0,   max: 1500,  label: 'углеводы' },
  fiber:    { min: 0,   max: 200,   label: 'клетчатка' },
} as const

type FieldKey = keyof typeof RANGES

function parseNum(s: string): number | undefined {
  const trimmed = s.trim()
  if (trimmed === '') return undefined
  const n = Number(trimmed)
  return Number.isFinite(n) ? n : undefined
}

function validate(values: DayNutritionValues): Partial<Record<FieldKey, string>> {
  const errors: Partial<Record<FieldKey, string>> = {}

  // Calories required
  const kcal = parseNum(values.calories)
  if (kcal === undefined) {
    errors.calories = 'Укажи калории (минимум 200 ккал)'
  } else if (kcal < RANGES.calories.min) {
    errors.calories = `Слишком мало — минимум ${RANGES.calories.min} ккал/день`
  } else if (kcal > RANGES.calories.max) {
    errors.calories = `Слишком много — максимум ${RANGES.calories.max} ккал`
  }

  // Macros: optional, but if provided — must be in range
  for (const key of ['protein', 'fat', 'carbs', 'fiber'] as const) {
    const raw = values[key]
    if (raw.trim() === '') continue
    const n = parseNum(raw)
    if (n === undefined) {
      errors[key] = 'Введи число'
      continue
    }
    if (n < RANGES[key].min) {
      errors[key] = `Не может быть меньше ${RANGES[key].min}`
    } else if (n > RANGES[key].max) {
      errors[key] = `Не может быть больше ${RANGES[key].max} г`
    }
  }

  return errors
}

export function DayNutritionSheet({ open, onClose, date, initialValues, enabledMetrics, metricsInitial, onEnabledChange, fatSecretConnected }: Props) {
  const queryClient = useQueryClient()
  const { data: dashboard } = useQuery({ queryKey: ['dashboard'], queryFn: () => api.getDashboard() })
  const curator = (dashboard as { curator?: { name?: string } | null } | undefined)?.curator
  const botUsername = (dashboard as { botUsername?: string | null } | undefined)?.botUsername
  const curatorChatUrl = curator && botUsername ? `https://t.me/${botUsername}` : null
  const [kcal, setKcal] = useState(initialValues.calories)
  const [protein, setProtein] = useState(initialValues.protein)
  const [fat, setFat] = useState(initialValues.fat)
  const [carbs, setCarbs] = useState(initialValues.carbs)
  const [fiber, setFiber] = useState(initialValues.fiber)
  const [fiberInfoOpen, setFiberInfoOpen] = useState(false)
  const [addMetricsOpen, setAddMetricsOpen] = useState(false)
  // Локальный набор включённых метрик (синкается с пропом при открытии) + значения
  // доп. метрик (кроме клетчатки — она в своём поле fiber/column).
  const [enabled, setEnabled] = useState<string[]>(enabledMetrics ?? ['fiber'])
  const [metricVals, setMetricVals] = useState<Record<string, string>>({})
  const [touched, setTouched] = useState(false)
  const [showCongrats, setShowCongrats] = useState(false)
  // Были ли калории за день уже заполнены на момент открытия. Если да — это
  // редактирование, и сохранение показывает тихий тост вместо праздничного
  // поздравления с «наградой» (как у замеров).
  const wasFilledOnOpenRef = useRef(false)

  const openCuratorChat = () => {
    if (!curatorChatUrl) return
    const tg = (window as { Telegram?: { WebApp?: { openTelegramLink?: (url: string) => void; close?: () => void } } }).Telegram?.WebApp
    if (tg?.openTelegramLink) tg.openTelegramLink(curatorChatUrl)
    else if (!tg) window.open(curatorChatUrl, '_blank')
    tg?.close?.()
  }

  useEffect(() => {
    if (!open) return
    setKcal(initialValues.calories)
    setProtein(initialValues.protein)
    setFat(initialValues.fat)
    setCarbs(initialValues.carbs)
    setFiber(initialValues.fiber)
    setEnabled(enabledMetrics ?? ['fiber'])
    const mv: Record<string, string> = {}
    for (const [k, v] of Object.entries(metricsInitial ?? {})) {
      if (typeof v === 'number') mv[k] = String(v)
    }
    setMetricVals(mv)
    setTouched(false)
    wasFilledOnOpenRef.current = parseNum(initialValues.calories) !== undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, date])

  const errors = useMemo(
    () => validate({ calories: kcal, protein, fat, carbs, fiber }),
    [kcal, protein, fat, carbs, fiber],
  )
  const hasErrors = Object.keys(errors).length > 0

  const saveMutation = useMutation({
    mutationFn: () => {
      const num = parseNum
      // Значения доп. метрик (кроме клетчатки): пустое поле → null (очистить).
      const metrics: Record<string, number | null> = {}
      for (const key of enabled) {
        if (key === 'fiber') continue
        const v = num(metricVals[key] ?? '')
        metrics[key] = v !== undefined ? v : null
      }
      return api.createDiaryEntry({
        date,
        ...(num(kcal) !== undefined ? { calories: num(kcal) } : {}),
        ...(num(protein) !== undefined ? { protein: num(protein) } : {}),
        ...(num(fat) !== undefined ? { fat: num(fat) } : {}),
        ...(num(carbs) !== undefined ? { carbs: num(carbs) } : {}),
        ...(num(fiber) !== undefined ? { fiber: num(fiber) } : {}),
        ...(Object.keys(metrics).length > 0 ? { metrics } : {}),
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['diary'] })
      await queryClient.invalidateQueries({ queryKey: ['actions', 'daily-status'] })
      if (wasFilledOnOpenRef.current) {
        toast.success('Изменения питания сохранены')
        onClose()
      } else {
        setShowCongrats(true)
      }
    },
    onError: () => toast.error('Не удалось сохранить'),
  })

  const handleSave = () => {
    setTouched(true)
    if (hasErrors) {
      toast.error('Исправь подсвеченные поля')
      return
    }
    saveMutation.mutate()
  }

  // Включение/выключение метрики (PUT) — оптимистично + синк с сервером.
  const updateMetricsMutation = useMutation({
    mutationFn: (next: string[]) => api.updateNutritionMetrics(next),
    onSuccess: (res) => {
      setEnabled(res.enabled)
      onEnabledChange?.(res.enabled)
      queryClient.invalidateQueries({ queryKey: ['nutrition-metrics'] })
    },
    onError: () => toast.error('Не удалось обновить метрики'),
  })
  const handleAddMetric = (key: string) => {
    setAddMetricsOpen(false)
    if (enabled.includes(key)) return
    const next = [...enabled, key]
    setEnabled(next)
    updateMetricsMutation.mutate(next)
  }
  const handleRemoveMetric = (key: string) => {
    const next = enabled.filter((k) => k !== key)
    setEnabled(next)
    setMetricVals((p) => { const c = { ...p }; delete c[key]; return c })
    updateMetricsMutation.mutate(next)
  }
  // Метрики в порядке реестра (а не в порядке добавления).
  const orderedMetrics = NUTRITION_METRICS.filter((m) => enabled.includes(m.key))

  const handleCongratsClose = () => {
    setShowCongrats(false)
    onClose()
  }

  const dragControls = useDragControls()
  const handleDragEnd = (_: unknown, info: PanInfo) => {
    if (info.offset.y > 80) onClose()
  }

  const showField = (field: FieldKey) => touched ? errors[field] : undefined

  return (
    <>
      <AnimatePresence>
        {open && !showCongrats && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose}
            style={{ position: 'fixed', inset: 0, zIndex: 410, background: 'rgba(0,0,0,0.45)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', fontFamily: F }}
          >
            <motion.div
              data-testid="day-nutrition-sheet"
              initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 300 }}
              drag="y" dragListener={false} dragControls={dragControls}
              dragConstraints={{ top: 0 }} dragElastic={0.1}
              onDragEnd={handleDragEnd}
              onClick={(e) => e.stopPropagation()}
              style={{ background: '#F5F6FA', borderTopLeftRadius: 28, borderTopRightRadius: 28, paddingBottom: 'calc(20px + env(safe-area-inset-bottom, 0px))', maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}
            >
              <div
                onPointerDown={(e) => dragControls.start(e)}
                style={{ display: 'flex', justifyContent: 'center', paddingTop: 10, paddingBottom: 4, cursor: 'grab', touchAction: 'none' }}
              >
                <SheetGrip onClose={onClose} />
              </div>

              <div style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', padding: '12px 20px 6px', gap: 12 }}>
                <div style={{ width: 44, height: 44, borderRadius: 14, background: 'rgba(108,92,231,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Utensils size={22} color="#6C5CE7" />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#1C2D41' }}>Питание за день</div>
                  <div style={{ fontSize: 12, color: '#9AA5B4', marginTop: 1 }}>Итого за весь день</div>
                </div>
                <button
                  data-testid="day-nutrition-close"
                  onClick={onClose}
                  style={{ width: 32, height: 32, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                >
                  <X size={16} color="#6B7B8D" />
                </button>
              </div>

              {!fatSecretConnected && (
                <div style={{ margin: '8px 16px 14px', borderRadius: 12, padding: '8px 12px', background: 'rgba(108,92,231,0.06)', border: '1px solid rgba(108,92,231,0.12)' }}>
                  <p style={{ fontSize: 12, color: '#6C5CE7', margin: 0, lineHeight: 1.5 }}>
                    💡 Подключи <b>FatSecret</b>, чтобы отслеживать питание по приёмам пищи и блюдам
                  </p>
                </div>
              )}

              <div style={{ padding: '0 16px' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#1C2D41', marginBottom: 10 }}>Калории и КБЖУ</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                  <NutrField label="Калории" value={kcal} unit="ккал" color="#4A9FE5" testid="day-nutrition-kcal" error={showField('calories')} onChange={setKcal} />
                  <NutrField label="Белки" value={protein} unit="г" color="#34C759" testid="day-nutrition-protein" error={showField('protein')} onChange={setProtein} />
                  <NutrField label="Жиры" value={fat} unit="г" color="#FF9500" testid="day-nutrition-fat" error={showField('fat')} onChange={setFat} />
                  <NutrField label="Углеводы" value={carbs} unit="г" color="#AF52DE" testid="day-nutrition-carbs" error={showField('carbs')} onChange={setCarbs} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {orderedMetrics.map((m) => (
                    m.key === 'fiber' ? (
                      <NutrField
                        key="fiber"
                        label={m.label} value={fiber} unit={m.unit} color={m.color}
                        testid="day-nutrition-fiber" error={showField('fiber')}
                        onChange={setFiber}
                        onInfo={() => setFiberInfoOpen(true)}
                        infoLabel="Как добрать?"
                        onRemove={() => handleRemoveMetric('fiber')}
                      />
                    ) : (
                      <NutrField
                        key={m.key}
                        label={m.label} value={metricVals[m.key] ?? ''} unit={m.unit} color={m.color}
                        testid={`day-nutrition-metric-${m.key}`}
                        onChange={(v) => { setMetricVals((p) => ({ ...p, [m.key]: v })); setTouched(true) }}
                        onRemove={() => handleRemoveMetric(m.key)}
                      />
                    )
                  ))}
                  {enabled.length < NUTRITION_METRICS.length && (
                    <button
                      type="button"
                      data-testid="add-metrics-open"
                      onClick={() => setAddMetricsOpen(true)}
                      style={{
                        width: '100%', height: 46, borderRadius: 14,
                        border: '1.5px dashed rgba(108,92,231,0.4)', background: 'rgba(108,92,231,0.04)',
                        color: '#6C5CE7', fontSize: 14, fontWeight: 700, fontFamily: F, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      }}
                    >
                      <Plus size={16} /> Добавить другие метрики
                    </button>
                  )}
                </div>

                <button
                  data-testid="day-nutrition-save"
                  onClick={handleSave}
                  disabled={saveMutation.isPending}
                  style={{
                    width: '100%', height: 52, borderRadius: 16, border: 'none',
                    background: saveMutation.isPending
                      ? 'rgba(26,58,92,0.1)'
                      : 'linear-gradient(135deg,#6C5CE7,#A29BFE)',
                    color: saveMutation.isPending ? 'rgba(26,58,92,0.3)' : 'white',
                    fontSize: 16, fontWeight: 700,
                    cursor: saveMutation.isPending ? 'default' : 'pointer',
                    fontFamily: F, marginTop: 18,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    opacity: 1,
                  }}
                >
                  {saveMutation.isPending ? 'Сохраняем…' : (
                    <>
                      <Check size={18} /> Сохранить питание
                    </>
                  )}
                </button>
                {touched && hasErrors && (
                  <p style={{ fontSize: 12, color: '#FF3B30', textAlign: 'center', margin: '8px 0 0', fontWeight: 600 }}>
                    Исправь подсвеченные поля
                  </p>
                )}
                {!touched && (
                  <p style={{ fontSize: 12, color: '#B0BEC5', textAlign: 'center', margin: '8px 0 0' }}>
                    Калории — обязательно. КБЖУ — опционально, но желательно
                  </p>
                )}
              </div>
              {/* Дополнительный отступ снизу — чтобы при выезде клавиатуры
                  scrollIntoView мог поместить нижние поля в центр окна */}
              <div style={{ height: 'calc(60vh - 80px)' }} />
              </div>{/* /scroll body */}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <RewardCongrats
        open={showCongrats}
        onClose={handleCongratsClose}
        icon={Utensils}
        title="Питание зафиксировано!"
        subtitle={`Калории и КБЖУ за день сохранены.\nТак держать — питайся осознанно.`}
        xp={date < new Date().toLocaleDateString('en-CA') ? 0 : 10}
        note={date < new Date().toLocaleDateString('en-CA') ? 'За прошлые дни прогресс не начисляется' : undefined}
        gradient="linear-gradient(135deg, #6C5CE7 0%, #A29BFE 100%)"
        ctaLabel={curatorChatUrl ? 'Отправить фото отчёт куратору' : 'Продолжить'}
        onCtaClick={curatorChatUrl ? openCuratorChat : undefined}
        testId="modal-nutrition-congrats"
      />

      <FiberInfoSheet open={fiberInfoOpen} onClose={() => setFiberInfoOpen(false)} />
      <AddMetricsSheet
        open={addMetricsOpen}
        onClose={() => setAddMetricsOpen(false)}
        enabled={enabled}
        onAdd={handleAddMetric}
      />
    </>
  )
}
